import groq from "groq";
import { leadFlowProductionReadyFilter } from "./lead-flow-runtime-resolution";

const leadFlowRichTextFields = `_type == "leadFlowRichText" => { text }`;

const leadFlowIconItemFields = `_type == "leadFlowIconItem" => {
  icon,
  "customIconUrl": customIcon.asset->url,
  caption
}`;

const leadFlowContentFields = `
  _key, _type,
  ${leadFlowRichTextFields},
  ${leadFlowIconItemFields},
  _type == "leadFlowReviewStars" => {
    source,
    starVariant,
    reviewPlatform,
    reviewText,
    ariaLabel,
    manualRating,
    manualReviewCount
  },
  _type == "leadFlowPhoneCta" => {
    label,
    icon,
    "customIconUrl": customIcon.asset->url
  },
  _type == "leadFlowAnswerSummary" => { heading, answers },
  _type == "testimonialItem" => { name, text },
  _type == "leadFlowContentGrid" => {
    name,
    columns[]{
      _key,
      items[]{ _key, _type, ${leadFlowRichTextFields}, ${leadFlowIconItemFields} }
    }
  }
`;

const leadFlowChoiceQuestionFields = `
  label,
  helperText,
  arrangement,
  options[]{
    _key,
    value,
    label,
    description,
    "iconUrl": icon.asset->url
  }
`;

const leadFlowQuestionFields = `
  _type == "leadFlowCareForQuestion" => {
    ${leadFlowChoiceQuestionFields},
    defaultAnswer,
    autoAdvance
  },
  _type == "leadFlowCareTypeQuestion" => {
    ${leadFlowChoiceQuestionFields},
    defaultAnswers
  },
  _type == "leadFlowUrgencyQuestion" => {
    ${leadFlowChoiceQuestionFields},
    defaultAnswer,
    autoAdvance
  },
  _type == "leadFlowPostalCodeQuestion" => { helperText, label, placeholder },
  _type == "leadFlowContactQuestion" => {
    fields[]{ _key, field, label, placeholder },
    showPrivacyDisclaimer
  }
`;

/**
 * Loads one lead flow for the runtime. Step entries are flattened onto their
 * resolved step: each step keeps its own `_type` (`leadFlowStep` or
 * `leadFlowInterstitialStep`) and takes the entry row's `_key`, which stays
 * stable when the step document is swapped or reordered.
 */
const LeadFlowByIdQuery = groq`
  *[_type == "leadFlow" && _id == $id && siteId == $siteId && ${leadFlowProductionReadyFilter}][0]{
    _id,
    versionId,
    formType,
    deliveryContext,
    enabled,
    presentation{
      progressIndicator,
      showProgressOnFirstStep,
      questionStyle{
        selectionIndicator,
        multiSelectFormat
      }
    },
    stepTransition{ enabled, variant, speed },
    thankYou{ content[]{ ${leadFlowContentFields} } },
    steps[]{
      _key,
      theme,
      _type == "leadFlowStepSettings" => {
        layout,
        panelOrder{ large, small }
      },
      ...step->{
        _type,
        id,
        name,
        _type == "leadFlowStep" => {
          actionLabel,
          items[]{ ${leadFlowContentFields}, ${leadFlowQuestionFields} },
          infoPanel{ content[]{ ${leadFlowContentFields} } }
        },
        _type == "leadFlowInterstitialStep" => {
          content[]{ ${leadFlowContentFields} },
          autoAdvance{ enabled, durationSeconds },
          continueLabel
        }
      }
    }
  }
`;

/**
 * Every lead flow on a site for the debug page's picker. Unlike
 * `LeadFlowByIdQuery` it skips the production-ready filter, so disabled or
 * incomplete flows are listed and the debug page can say why they fail.
 */
const LeadFlowDebugListQuery = groq`
  *[_type == "leadFlow" && siteId == $siteId] | order(versionId asc){
    _id,
    internalName,
    versionId,
    enabled,
    deliveryContext
  }
`;

const LeadFlowDebugSectionListQuery = groq`
  *[_type == "leadFlowDebugSection" && siteId == $siteId] | order(internalName asc){
    _id,
    internalName
  }
`;

/** Matches the route page projection, which spreads every block as authored. */
const LeadFlowDebugSectionByIdQuery = groq`
  *[_type == "leadFlowDebugSection" && _id == $id && siteId == $siteId][0]{
    _id,
    internalName,
    pageBuilder[]{
      ...
    }
  }
`;

export {
  LeadFlowByIdQuery,
  LeadFlowDebugListQuery,
  LeadFlowDebugSectionByIdQuery,
  LeadFlowDebugSectionListQuery,
};
